import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  WRAP_CONTENT_PADDING_X,
  WRAP_CONTENT_PADDING_Y,
  computeWrapBands,
  roundedBoxRowBands,
} from '@/lib/text/compute-wrap-obstacles'
import type { WrapBand } from '@/lib/text/types/wrap-band'
import { computeRowFreeSegments } from '@/lib/text/wrap-row-layout'
import { isRoundingEligibleShape } from '@/lib/object-effects/rounding'
import {
  type ShapeTextFlow,
  type ShapeTextSegment,
  intersectSegments,
} from '@/lib/shapes/shape-text'
import type { ShapeTextWrapContext } from '@/lib/shapes/types/shape-text-wrap-context'

/**
 * Text-wrap-around-obstacles for the shape-text (scanline) engine — the consumer half of wrap that
 * only ever existed in the deleted contentEditable engine (`text-renderer.tsx`). See
 * `docs/text-unification.md` Phase 5a and `docs/text-wrap-dev-baseline.md` for the measured `dev`
 * behavior this restores.
 *
 * The *source* half is untouched and reused wholesale: `computeWrapBands`
 * (`lib/text/compute-wrap-obstacles.ts`) already resolves which nearby shapes are wrap sources,
 * honours z-order, group live-union rects, group `wrap` leaf decomposition, per-side offsets, and
 * the target's own `ignoreTextWrap`. Only the consumer half was missing.
 *
 * **Why this lives above the boundary, not inside the flow kernel.** `getShapeTextFlow` has both a
 * TS and a Go/WASM implementation (`src/wasm/shape-text.go`). Subtracting bands inside the kernel
 * would mean keeping two implementations in lockstep for every future wrap change. Instead this
 * transforms the `ShapeTextFlow` the kernel already produced, which is pure interval arithmetic on
 * `rows[].segments` and needs no knowledge of path geometry at all.
 *
 * **The invariant that makes that safe:** `fitParagraphsToShape`'s "measure with the engine that
 * draws" rule. Because the subtraction happens to the flow *before* `layoutShapeTextContent`
 * consumes it, measurement and paint see the identical row segments — provided every flow call site
 * applies the same bands. Phase 5a named three (measure `fit-paragraphs-to-shape.ts` / render
 * `use-shape-text-layout.ts` / save `bake-shape-text-wire-lines.ts`); `text-to-shape-outline.ts` has
 * since become a fourth, and it passes no `wrap` context of its own.
 */

/** Bands and flow rows disagree about their origin by a hair, and this is the reconciliation. */
const EDGE_EPSILON = 0.01

/**
 * Flags each resulting segment's `endIsWrapObstacle` when a band actually pulled its right edge in
 * from the ORIGINAL (pre-band) segment that contains it — as opposed to the result simply
 * inheriting that original segment's own edge unchanged (the shape's own contour/frame edge, or
 * narrowing already baked in by `applyRoundedCornerToShapeTextFlow` before this function ever
 * runs). This is what lets `layoutShapeTextContent` tell "a word ran into an obstacle" apart from
 * "a word reached the end of an ordinary line" using nothing but the segment list it already reads.
 */
function markWrapObstacleEdges(
  original: ShapeTextSegment[],
  result: ShapeTextSegment[]
): ShapeTextSegment[] {
  return result.map((seg) => {
    const parent = original.find(
      (o) => o.startX <= seg.startX + EDGE_EPSILON && o.endX >= seg.endX - EDGE_EPSILON
    )

    return parent && seg.endX < parent.endX - EDGE_EPSILON
      ? { ...seg, endIsWrapObstacle: true }
      : seg
  })
}

/**
 * The single entry point every flow consumer should use: narrow `flow` for the shape's own rounded
 * corners, then subtract any external wrap-source bands, in one call.
 *
 * Deliberately one function rather than exposing `applyRoundedCornerToShapeTextFlow`,
 * `computeShapeTextWrapBands` and `applyWrapBandsToShapeTextFlow` separately to call sites.
 * `fitParagraphsToShape`'s "measure with the engine that draws" invariant only holds if the
 * measure, render and save paths derive both corrections *identically*; funnelling all three call
 * sites through here makes divergence impossible by construction rather than by convention.
 *
 * Rounding is unconditional (it's a no-op unless `shape.roundedCorner > 0`); `wrap` stays optional,
 * same as before — omit it and only the shape's own rounding applies.
 */
export function shapeTextFlowWithWrap(
  shape: ShapeData,
  flow: ShapeTextFlow,
  wrap?: ShapeTextWrapContext
): ShapeTextFlow {
  const rounded = applyRoundedCornerToShapeTextFlow(flow, shape)

  if (!wrap) return rounded

  return applyWrapBandsToShapeTextFlow(
    rounded,
    computeShapeTextWrapBands(shape, wrap.allShapes, wrap.zIndexOf),
    shape.width
  )
}

/**
 * Narrows each row's free segments by the frame's own rounded corners (`ShapeData.roundedCorner`) —
 * lost in text-unification when a rectangle's rounding stayed a shape-renderer-only visual effect
 * (`shape-renderer/hooks/use-shape-path.ts`'s `shapePath`) with no equivalent for the shape-text (scanline) engine, which
 * always measured against the plain axis-aligned box regardless of rounding.
 *
 * Rounding only ever applied to rectangle/square shapes (`isRoundingEligibleShape` — kindId 1), same
 * restriction as the Effects panel control itself; every other shape kind is returned unchanged.
 *
 * Reuses `roundedBoxRowBands` — the exact corner-containment geometry `fitParagraphsToRoundedBox`
 * (the old TextBoxData rounded-frame fitter, `lib/text/fit-paragraphs-to-rounded-box.ts`) already
 * used, since a corner's containment math doesn't depend on which engine draws the text. Unlike an
 * external wrap obstacle, a corner's inset is a *continuous* function of row position (see
 * `roundedBoxRowInset`) rather than a fixed rectangle — there is no discrete "obstacle bottom" to
 * resume text at, so a row squeezed to zero width (only reachable at very high rounding percentages
 * on a narrow frame) is simply dropped, with none of `applyWrapBandsToShapeTextFlow`'s row-shift
 * bookkeeping needed.
 */
export function applyRoundedCornerToShapeTextFlow(
  flow: ShapeTextFlow,
  shape: ShapeData
): ShapeTextFlow {
  if (!isRoundingEligibleShape(shape)) return flow
  if (typeof shape.roundedCorner !== 'number' || shape.roundedCorner <= 0) return flow

  const rows: ShapeTextFlow['rows'] = []

  for (const row of flow.rows) {
    const bands = roundedBoxRowBands(
      shape.width,
      shape.height,
      shape.roundedCorner,
      row.yTop,
      row.yTop + flow.rowHeight
    )

    if (bands.length === 0) {
      rows.push(row)
      continue
    }

    const free = computeRowFreeSegments(bands, row.yTop, row.yTop + flow.rowHeight, shape.width)

    if (free.length === 0) continue

    const freeSegments: ShapeTextSegment[] = free.map((seg) => ({
      startX: seg.start,
      endX: seg.end,
    }))
    const segments = intersectSegments(row.segments, freeSegments)

    if (segments.length === 0) continue

    rows.push({ ...row, segments })
  }

  return { ...flow, rows }
}

/**
 * Wrap bands affecting `shape` as a text frame, expressed in **shape-local** coordinates so they can
 * be subtracted directly from `ShapeTextFlow.rows[].segments`.
 *
 * `computeWrapBands` reports bands relative to a *content* box inset by `WRAP_CONTENT_PADDING_X/Y`
 * (~1-2pt — `TEXT_BOX_PADDING` is defined from the `SHAPE_TEXT_INSET_*` constants), and clamps them
 * to that box. Flow rows are shape-local, and already carry the text inset themselves. So each band
 * is shifted back out by that padding.
 *
 * The shift alone would leave a ~1-2pt sliver of "free" space at any edge the band was clamped to —
 * sub-glyph, but enough to survive `MIN_USABLE_FREE_SEGMENT_PX`. Hence the edge snapping: a band that
 * reached a content-box edge is one that truly extended to (or past) the frame edge, so it is
 * re-extended to the shape edge rather than shifted. The overlap test itself is deliberately left on
 * the shape's true rect — expanding the candidate to dodge this would have quietly widened which
 * shapes count as sources at all.
 */
export function computeShapeTextWrapBands(
  shape: ShapeData,
  allShapes: ShapeData[],
  zIndexOf: (id: string) => number | undefined
): WrapBand[] {
  if (shape.width <= 0 || shape.height <= 0) return []

  const contentWidth = Math.max(0, shape.width - WRAP_CONTENT_PADDING_X * 2)
  const contentHeight = Math.max(0, shape.height - WRAP_CONTENT_PADDING_Y * 2)

  const bands = computeWrapBands(
    {
      id: shape.id,
      x: shape.x,
      y: shape.y,
      width: shape.width,
      height: shape.height,
      rotation: shape.rotation,
      parentGroupId: shape.parentGroupId,
      hidden: shape.hidden,
      ignoreTextWrap: shape.ignoreTextWrap,
    },
    allShapes,
    zIndexOf
  )

  return bands.map((band) => ({
    left: band.left <= EDGE_EPSILON ? 0 : band.left + WRAP_CONTENT_PADDING_X,
    right:
      band.right >= contentWidth - EDGE_EPSILON ? shape.width : band.right + WRAP_CONTENT_PADDING_X,
    top: band.top <= EDGE_EPSILON ? 0 : band.top + WRAP_CONTENT_PADDING_Y,
    bottom:
      band.bottom >= contentHeight - EDGE_EPSILON
        ? shape.height
        : band.bottom + WRAP_CONTENT_PADDING_Y,
  }))
}

/**
 * Whether `band` is actually necessary to keep at least one row in `rows` within `[from, to)`
 * fully covered — i.e. whether `band`, ON ITS OWN, leaves some row in that range with zero free
 * space. Row coverage is decided per whole grid row (`computeRowFreeSegments` excludes a band's
 * full x-range across a row the instant the band overlaps ANY part of that row's height, not just
 * the overlapping fraction) — so a band merely SHARING vertical space with a run, without ever
 * BY ITSELF being enough to block any of the run's rows, must not be credited with defining that
 * run's real top/bottom edge. This is what distinguishes a Jump obstacle's own full-width band
 * (always necessary, alone, everywhere it's active) from an unrelated partial-width Bounding-Box
 * obstacle that happens to occupy the same rows without ever independently blocking one — see the
 * multi-style-obstacle regression test below.
 *
 * Deliberately tests `band` in isolation rather than "removing it from `bands`, does some row
 * become uncovered" — the latter breaks down the moment TWO bands are each independently
 * sufficient to cover the exact same run (e.g. two overlapping/nested Jump obstacles): removing
 * either one still leaves the OTHER covering everything, so both would be disqualified as "not
 * necessary" and `coveringBottomEdge` would fall back to `from` instead of the bands' real
 * bottom edge — producing a shift equal to the whole run's span, which shoves every row after the
 * run back into the obstacles. Testing `band` alone is immune to that redundancy and still gives
 * the same answer for the partial-width case, since a partial-width band alone never fully covers
 * a row regardless of what else is present.
 */
function isBandNecessaryForRun(
  band: WrapBand,
  rows: ShapeTextFlow['rows'],
  rowHeight: number,
  from: number,
  to: number,
  shapeWidth: number
): boolean {
  return rows.some(
    (row) =>
      row.yTop >= from &&
      row.yTop < to &&
      computeRowFreeSegments([band], row.yTop, row.yTop + rowHeight, shapeWidth).length === 0
  )
}

/** The real bottom edge of whichever bands are actually necessary to fully cover the dropped run
 *  `[from, to)` — see `isBandNecessaryForRun`. Falls back to `from` (no shift) when nothing in
 *  `bands` turns out to be necessary, which cannot happen for a genuine dropped run but keeps this
 *  total. */
function coveringBottomEdge(
  bands: WrapBand[],
  rows: ShapeTextFlow['rows'],
  rowHeight: number,
  from: number,
  to: number,
  shapeWidth: number
): number {
  const necessary = bands.filter(
    (b) =>
      b.top < to &&
      b.bottom > from &&
      isBandNecessaryForRun(b, rows, rowHeight, from, to, shapeWidth)
  )

  return Math.max(from, ...necessary.map((b) => b.bottom))
}

/**
 * Subtracts `bands` from every row of `flow`, returning a new flow whose `rows[].segments` describe
 * only the space text may actually occupy. Returns `flow` unchanged when there is nothing to subtract,
 * so the no-wrap path stays reference-identical (and free).
 *
 * A row left with no usable space is **dropped**, not emptied — which is precisely how the `jump`
 * style works and why it needs no special case here: `jumpBandFromRect` spans the full content width
 * over the obstacle's vertical extent, so every row it covers loses all free space and text resumes
 * below the obstacle.
 *
 * `flow.rows` are a fixed, uniform-pitch grid the kernel laid out with no knowledge of wrap — dropping
 * a covered row from that grid without repositioning what follows would resume text at the next
 * `rowHeight`-quantized slot, not at the obstacle's real bottom edge. Any obstacle whose height isn't
 * an exact multiple of `rowHeight` (the overwhelmingly common case) would then leave a visible extra
 * gap — confirmed against the measured `dev` baseline (`docs/text-wrap-dev-baseline.md` Case 3: a
 * 61pt-tall obstacle with a 6pt offset resumes text at `top: 59`, not at a multiple of the 14pt row
 * pitch). So surviving rows are shifted up to close that quantization slack: for each contiguous run
 * of fully-covered rows, `shift` is (re)computed fresh as the difference between the whole rows
 * dropped and the band's own real extent, landing the resuming row exactly at the covering band's
 * true bottom — an ABSOLUTE shape-local coordinate, the same result the old row-by-row walker
 * (`computeLineRowSegments`'s `coveringBottomPt` jump, `wrap-row-layout.ts`) produced by construction.
 * `baselineY` moves by the same amount so painted glyphs and the shifted `yTop` stay in sync.
 *
 * Recomputed fresh per run, not accumulated across runs: with two or more separate obstacles on the
 * same frame, each one's resume position must land at its OWN real bottom edge regardless of
 * whatever shift an earlier obstacle's run left behind. Accumulating would leave every obstacle
 * after the first resuming short by the sum of every prior obstacle's saved slack.
 *
 * Rows before the first run are never moved (WMPCM-10115, AC 3.1). Jump affects only the rows its
 * band covers and the rows below them; a row above `object top − Top offset` keeps its exact
 * position, the same as under No Wrap. The Top offset still decides which rows are covered, so it
 * moves text a whole row at a time.
 */
export function applyWrapBandsToShapeTextFlow(
  flow: ShapeTextFlow,
  bands: WrapBand[],
  shapeWidth: number
): ShapeTextFlow {
  if (bands.length === 0) return flow

  const rows: ShapeTextFlow['rows'] = []
  let shift = 0
  let droppedRunStart: number | null = null

  // Closes the just-finished run of fully-dropped rows (if any), landing the resuming row exactly
  // at the covering band(s)' real (absolute) bottom edge.
  //
  // `shift` is reassigned here, not accumulated — each run's resume anchor is an ABSOLUTE
  // shape-local coordinate (the object's own real bottom edge), independent of whatever `shift` was
  // left over from an earlier run. A prior version used `shift +=`, carrying the previous run's leftover shift into this run's own anchor
  // math — correct only when there was exactly ONE obstacle (so `shift` was still 0 going in), and
  // silently wrong the moment a SECOND jump/wrap obstacle sat further down the same frame: its own
  // resume row landed at `coveringBottom - <first obstacle's shift>` instead of `coveringBottom`,
  // i.e. offset by however much the first obstacle had already saved. Reassigning here is exactly
  // equivalent to the old first-run-only `shift = 0` reset, just applied uniformly to every run
  // instead of special-cased for the first one.
  const closeDroppedRun = (resumeAtOriginalY: number) => {
    if (droppedRunStart == null) return

    const runSpan = resumeAtOriginalY - droppedRunStart
    const coveringBottom = coveringBottomEdge(
      bands,
      flow.rows,
      flow.rowHeight,
      droppedRunStart,
      resumeAtOriginalY,
      shapeWidth
    )

    shift = runSpan - (coveringBottom - droppedRunStart)
    droppedRunStart = null
  }

  for (const row of flow.rows) {
    const origTop = row.yTop
    const free = computeRowFreeSegments(bands, origTop, origTop + flow.rowHeight, shapeWidth)

    if (free.length === 0) {
      if (droppedRunStart == null) droppedRunStart = origTop
      continue
    }

    closeDroppedRun(origTop)

    const freeSegments: ShapeTextSegment[] = free.map((seg) => ({
      startX: seg.start,
      endX: seg.end,
    }))
    const segments = markWrapObstacleEdges(
      row.segments,
      intersectSegments(row.segments, freeSegments)
    )

    if (segments.length === 0) continue

    rows.push({
      ...row,
      yTop: origTop - shift,
      baselineY: row.baselineY - shift,
      segments,
    })
  }

  return { ...flow, rows }
}
